// The wire protocol (docs/architecture/api.md). This file is the machine-readable source of truth:
// the Worker validates with these schemas, and the web app and extension use the inferred types.
import { z } from 'zod';

/** Sent as `X-PF-Schema`. The server accepts the current and the previous version (NFR-14). */
export const SCHEMA_VERSION = 10;
/**
 * The oldest client the server still serves. Version 1 clients are safe on a version 10 server:
 * they ignore tables they don't know, and every table they do know is unchanged.
 */
export const MIN_SCHEMA_VERSION = 1;
export const SCHEMA_HEADER = 'X-PF-Schema';
export const SESSION_COOKIE = 'pf_session';

// ── Errors ────────────────────────────────────────────────────────────────────
export const ERROR_STATUS = {
  bad_request: 400,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  payload_too_large: 413,
  schema_too_old: 426,
  rate_limited: 429,
  internal: 500,
  /** An upstream service (place search, media) is down or out of quota. */
  unavailable: 503,
} as const;
export type ErrorCode = keyof typeof ERROR_STATUS;
export const ErrorCodeSchema = z.enum(Object.keys(ERROR_STATUS) as [ErrorCode, ...ErrorCode[]]);
export const ErrorBody = z.object({ error: z.object({ code: ErrorCodeSchema, message: z.string() }) });
export type ErrorBody = z.infer<typeof ErrorBody>;

// ── Primitives ────────────────────────────────────────────────────────────────
/** UUIDv7, generated by clients. */
export const Id = z.uuid({ version: 'v7' });
/** `<ms:12 hex>-<counter:4 hex>-<node:8 hex>`; string order = clock order (T-PKG-01 fixes the encoding). */
export const Hlc = z.string().regex(/^[0-9a-f]{12}-[0-9a-f]{4}-[0-9a-f]{8}$/);
export const ClientId = z.string().regex(/^[0-9a-f]{8}$/);
export const Email = z.email().max(254);
export const Currency = z.string().regex(/^[A-Z]{3}$/);

// ── Health ────────────────────────────────────────────────────────────────────
export const HealthResponse = z.object({ ok: z.literal(true), schema: z.number().int(), env: z.string() });
export type HealthResponse = z.infer<typeof HealthResponse>;

// ── Auth (F-12) ───────────────────────────────────────────────────────────────
export const AuthCodeRequest = z.object({ email: Email });
export const Platform = z.enum(['ios', 'ipados', 'macos', 'web', 'extension']);
export const AuthVerifyRequest = z.object({
  email: Email,
  code: z.string().regex(/^\d{6}$/),
  device: z.object({ id: z.string().min(1).max(64), name: z.string().min(1).max(100), platform: Platform }),
});
export const User = z.object({ id: Id, email: Email, displayName: z.string(), homeCurrency: Currency });
export type User = z.infer<typeof User>;
/** Native clients get `token` (a bearer token for the Keychain). The web gets an HttpOnly cookie instead, and no token. */
export const AuthVerifyResponse = z.object({
  token: z.string().optional(),
  expiresAt: z.iso.datetime(),
  user: User,
});
export const SignOutRequest = z.object({ scope: z.enum(['device', 'all']) });
export const MeResponse = z.object({ user: User, partner: User.nullable() });
/** Profile edits (FR-AUTH-04): balances and settle-up follow the home currency (FR-BUD-15). */
export const UpdateMeRequest = z.object({
  displayName: z.string().trim().min(1).max(40).optional(),
  homeCurrency: Currency.optional(),
});

// ── Sync (F-11) ───────────────────────────────────────────────────────────────
/** Field values on the wire are plain JSON scalars; per-table validation happens server-side. */
export const FieldValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);
/** Wire table names are camelCase (`checklistEntry`, `importSource`); unknown ones are rejected per change. */
export const TableName = z.string().regex(/^[a-z][a-zA-Z]*$/);

export const Change = z.object({
  table: TableName,
  id: Id,
  tripId: Id,
  ownerUserId: Id.nullable(),
  hlc: Hlc,
  fields: z
    .record(z.string(), FieldValue)
    .refine((f) => Object.keys(f).length > 0, 'fields must not be empty'),
});
export type Change = z.infer<typeof Change>;

export const MAX_PUSH_CHANGES = 100;
/**
 * What clients send per push and get per pull page (T-TST-02, 2026-10-16). Measured Worker CPU on
 * Turso: a 25-change push p95 6.2 ms (100 changes: 15 ms); a 100-row pull 5–9 ms (500 rows: up to
 * 29 ms). The free plan allows 10 ms per request. The server still accepts 100-change pushes from
 * older builds, and clamps pull pages to PULL_PAGE.
 */
export const PUSH_BATCH = 25;
export const PULL_PAGE = 100;
export const MAX_PUSH_BYTES = 256 * 1024;
export const PushRequest = z.object({
  clientId: ClientId,
  changes: z.array(Change).min(1).max(MAX_PUSH_CHANGES),
});
export type PushRequest = z.infer<typeof PushRequest>;

export const RejectReason = z.enum([
  'not_member',
  'forbidden_owner',
  'parent_deleted',
  'invalid',
  'unknown_table',
  'immutable_field',
]);

export const MAX_PULL_LIMIT = 500;
export const PullQuery = z.object({
  since: z.coerce.number().int().nonnegative(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PULL_LIMIT)
    .default(PULL_PAGE)
    .transform((n) => Math.min(n, PULL_PAGE)),
});
export const RowSnapshot = z.object({
  table: TableName,
  id: Id,
  tripId: Id,
  ownerUserId: Id.nullable(),
  values: z.record(z.string(), FieldValue),
  hlc: z.record(z.string(), Hlc),
});
export type RowSnapshot = z.infer<typeof RowSnapshot>;

/**
 * `rows` carries the server's current version of every rejected row the caller can see, so the
 * device replaces its local copy (a rejected row it created and the server never had is absent:
 * the device drops it). Without this, a rejected local value would never be corrected.
 */
export const PushResponse = z.object({
  rejected: z.array(z.object({ id: Id, table: TableName, reason: RejectReason })),
  rows: z.array(RowSnapshot),
  cursor: z.number().int().nonnegative(),
});
export type PushResponse = z.infer<typeof PushResponse>;
export const PullResponse = z.object({
  rows: z.array(RowSnapshot),
  cursor: z.number().int().nonnegative(),
  more: z.boolean(),
});
export type PullResponse = z.infer<typeof PullResponse>;

export const TripBundle = z.object({ rows: z.array(RowSnapshot), cursor: z.number().int().nonnegative() });

// ── Live channel (FR-SYNC-09, FR-TRK-09) ──────────────────────────────────────
export const PositionFields = {
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  acc: z.number().nonnegative(),
  at: z.iso.datetime(),
};
export const ClientMessage = z.discriminatedUnion('t', [
  z.object({ t: z.literal('pos'), tripId: Id, ...PositionFields }),
]);
export const ServerMessage = z.discriminatedUnion('t', [
  z.object({ t: z.literal('cursor'), seq: z.number().int().nonnegative() }),
  z.object({ t: z.literal('pos'), userId: Id, ...PositionFields }),
  z.object({ t: z.literal('bye'), reason: z.enum(['session_revoked', 'signed_out']) }),
]);
export type ClientMessage = z.infer<typeof ClientMessage>;
export type ServerMessage = z.infer<typeof ServerMessage>;

// ── Places (ADR-0011) ─────────────────────────────────────────────────────────
/** OSM element reference: `N123`, `W336641107`, `R42`. */
export const OsmRef = z.string().regex(/^[NWR][0-9]{1,12}$/);
export const PlaceResult = z.object({
  name: z.string(),
  address: z.string().optional(),
  lat: z.number(),
  lng: z.number(),
  /** Provider category, e.g. `amenity.place_of_worship` (Photon) or `tourism.sights` (Geoapify). */
  category: z.string().optional(),
  osm: OsmRef.optional(),
  /** Geoapify's place ID: `/places/media?geoapify=` resolves it to the OSM element. */
  geoapify: z.string().optional(),
  source: z.enum(['geoapify', 'photon']),
});
export type PlaceResult = z.infer<typeof PlaceResult>;
export const PlaceSearchResponse = z.object({ results: z.array(PlaceResult) });

export const PlacePhoto = z.object({
  url: z.string(),
  /** Plain-text author, shown with the licence (Wikimedia) or "Mapillary · <user>". */
  credit: z.string(),
  license: z.string().optional(),
  licenseUrl: z.string().optional(),
  /** Where the photo lives: the Commons file page, or the Mapillary viewer. */
  pageUrl: z.string(),
  source: z.enum(['wikimedia', 'mapillary']),
  /** Mapillary image URLs expire; store this ID, not the URL. */
  mapillaryId: z.string().optional(),
});
export const PlaceMediaResponse = z.object({
  photo: PlacePhoto.nullable(),
  description: z.object({ text: z.string(), source: z.literal('wikipedia'), url: z.string() }).nullable(),
  /** OSM facts worth keeping on the Place row (names in other scripts, hours, website). */
  osm: z
    .object({
      wikidata: z.string().optional(),
      openingHours: z.string().optional(),
      website: z.string().optional(),
      names: z.record(z.string(), z.string()),
    })
    .nullable(),
});
export type PlaceMediaResponse = z.infer<typeof PlaceMediaResponse>;

// ── Offline maps (ADR-0004) ───────────────────────────────────────────────────
/** An offline map archive on R2, with what its PMTiles header says it covers. */
export const MapArchive = z.object({
  name: z.string(),
  bytes: z.number().int(),
  minZoom: z.number().int(),
  maxZoom: z.number().int(),
  /** [west, south, east, north] */
  bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  /** The matching offline place index (`/v1/pois/<name>.sqlite.gz`), if there is one. */
  poiBytes: z.number().int().nullable(),
});
export type MapArchive = z.infer<typeof MapArchive>;
export const MapCatalog = z.object({ maps: z.array(MapArchive) });
export type MapCatalog = z.infer<typeof MapCatalog>;
